import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  Script,
  ShowNotes,
  ShowProgress,
  ShowSummary,
  UserSettings,
  UserStats,
} from '@/types/domain';

export interface OutboxEntry {
  id?: number;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body: unknown;
  createdAt: string;
  attempts: number;
  /** Collapses repeated writes to the same resource into one queued entry. */
  dedupeKey: string;
}

interface LineRunnerDB extends DBSchema {
  shows: { key: string; value: ShowSummary };
  scripts: { key: string; value: Script };
  progress: { key: string; value: ShowProgress };
  notes: { key: string; value: { showId: string; notes: ShowNotes } };
  singletons: {
    key: string;
    value: { key: string; settings?: UserSettings; stats?: UserStats };
  };
  outbox: { key: number; value: OutboxEntry; indexes: { 'by-dedupe': string } };
  recordings: { key: string; value: { lineId: string; blob: Blob; createdAt: string } };
}

const DB_NAME = 'line-runner';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<LineRunnerDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<LineRunnerDB>> {
  dbPromise ??= openDB<LineRunnerDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore('shows', { keyPath: 'showId' });
      db.createObjectStore('scripts', { keyPath: 'showId' });
      db.createObjectStore('progress', { keyPath: 'showId' });
      db.createObjectStore('notes', { keyPath: 'showId' });
      db.createObjectStore('singletons', { keyPath: 'key' });
      const outbox = db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
      outbox.createIndex('by-dedupe', 'dedupeKey');
      db.createObjectStore('recordings', { keyPath: 'lineId' });
    },
  });
  return dbPromise;
}

// --- shows -----------------------------------------------------------------

export async function localListShows(): Promise<ShowSummary[]> {
  return (await getDb()).getAll('shows');
}

export async function localGetShow(showId: string): Promise<ShowSummary | undefined> {
  return (await getDb()).get('shows', showId);
}

export async function localPutShow(show: ShowSummary): Promise<void> {
  await (await getDb()).put('shows', show);
}

export async function localDeleteShow(showId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['shows', 'scripts', 'progress', 'notes'], 'readwrite');
  await Promise.all([
    tx.objectStore('shows').delete(showId),
    tx.objectStore('scripts').delete(showId),
    tx.objectStore('progress').delete(showId),
    tx.objectStore('notes').delete(showId),
    tx.done,
  ]);
}

// --- script / progress / notes ---------------------------------------------

export async function localGetScript(showId: string): Promise<Script | undefined> {
  return (await getDb()).get('scripts', showId);
}

export async function localPutScript(script: Script): Promise<void> {
  await (await getDb()).put('scripts', script);
}

export async function localGetProgress(showId: string): Promise<ShowProgress | undefined> {
  return (await getDb()).get('progress', showId);
}

export async function localPutProgress(progress: ShowProgress): Promise<void> {
  await (await getDb()).put('progress', progress);
}

export async function localGetNotes(showId: string): Promise<ShowNotes | undefined> {
  return (await getDb()).get('notes', showId).then((row) => row?.notes);
}

export async function localPutNotes(showId: string, notes: ShowNotes): Promise<void> {
  await (await getDb()).put('notes', { showId, notes });
}

// --- singletons ------------------------------------------------------------

export async function localGetSettings(): Promise<UserSettings | undefined> {
  return (await getDb()).get('singletons', 'settings').then((row) => row?.settings);
}

export async function localPutSettings(settings: UserSettings): Promise<void> {
  await (await getDb()).put('singletons', { key: 'settings', settings });
}

export async function localGetStats(): Promise<UserStats | undefined> {
  return (await getDb()).get('singletons', 'stats').then((row) => row?.stats);
}

export async function localPutStats(stats: UserStats): Promise<void> {
  await (await getDb()).put('singletons', { key: 'stats', stats });
}

// --- recordings ------------------------------------------------------------

export async function localPutRecording(lineId: string, blob: Blob): Promise<void> {
  await (await getDb()).put('recordings', { lineId, blob, createdAt: new Date().toISOString() });
}

export async function localGetRecording(lineId: string): Promise<Blob | undefined> {
  return (await getDb()).get('recordings', lineId).then((row) => row?.blob);
}

export async function localDeleteRecording(lineId: string): Promise<void> {
  await (await getDb()).delete('recordings', lineId);
}

// --- outbox ----------------------------------------------------------------

export async function enqueue(entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts'>): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('outbox', 'readwrite');
  const index = tx.store.index('by-dedupe');

  // Only the newest write to a resource matters; drop superseded ones.
  let cursor = await index.openCursor(IDBKeyRange.only(entry.dedupeKey));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }

  await tx.store.add({ ...entry, createdAt: new Date().toISOString(), attempts: 0 });
  await tx.done;
}

export async function peekOutbox(): Promise<OutboxEntry[]> {
  return (await getDb()).getAll('outbox');
}

export async function dropOutboxEntry(id: number): Promise<void> {
  await (await getDb()).delete('outbox', id);
}

export async function markOutboxAttempt(entry: OutboxEntry): Promise<void> {
  if (entry.id === undefined) return;
  await (await getDb()).put('outbox', { ...entry, attempts: entry.attempts + 1 });
}

export async function outboxSize(): Promise<number> {
  return (await getDb()).count('outbox');
}

export async function clearAllLocalData(): Promise<void> {
  const db = await getDb();
  const stores = ['shows', 'scripts', 'progress', 'notes', 'singletons', 'outbox', 'recordings'] as const;
  const tx = db.transaction(stores, 'readwrite');
  await Promise.all([...stores.map((store) => tx.objectStore(store).clear()), tx.done]);
}
